/**
 * 🛡️ G2GENTE - Configurações do Candidato (Web)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import CandidateLayout from '../../components/CandidateLayout';
import { candidatesAPI, lgpdAPI, authAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { 
  Settings,
  Loader2,
  Shield,
  Bell,
  Eye,
  Lock,
  Trash2,
  LogOut,
  Download,
  KeyRound,
  X,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

export default function CandidateSettings() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [consents, setConsents] = useState({});
  const [exporting, setExporting] = useState(false);
  const [deletionConfirm, setDeletionConfirm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [settings, setSettings] = useState({
    consent_share: true,
    notifications: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, consentsRes] = await Promise.all([
        candidatesAPI.getMe(),
        lgpdAPI.getConsents()
      ]);
      
      setSettings(s => ({
        ...s,
        consent_share: profileRes.data.consent_share !== false
      }));
      
      const consentsMap = {};
      consentsRes.data.forEach(c => {
        consentsMap[c.consent_type] = c.granted;
      });
      setConsents(consentsMap);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConsentChange = async (value) => {
    try {
      await candidatesAPI.updateMe({ consent_share: value });
      setSettings(s => ({ ...s, consent_share: value }));
      toast.success(value ? 'Perfil visível na busca' : 'Perfil oculto da busca');
    } catch (error) {
      toast.error('Erro ao atualizar configuração');
    }
  };

  const handleLGPDConsentChange = async (consentType, value) => {
    try {
      await lgpdAPI.updateConsent(consentType, value);
      setConsents(c => ({ ...c, [consentType]: value }));
      toast.success('Preferência atualizada');
    } catch (error) {
      toast.error('Erro ao atualizar preferência');
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setChangingPassword(true);
    try {
      await authAPI.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success('Senha alterada com sucesso!');
      setShowPasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Senha atual incorreta');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await lgpdAPI.exportData();
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `g2egente-meus-dados-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Dados exportados com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar dados');
    } finally {
      setExporting(false);
    }
  };

  const handleDeletionRequest = async () => {
    try {
      await lgpdAPI.requestDeletion();
      toast.success('Solicitação de exclusão enviada. Você será notificado.');
      setDeletionConfirm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao solicitar exclusão');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <CandidateLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </CandidateLayout>
    );
  }

  return (
    <CandidateLayout>
      <div className="max-w-3xl space-y-6" data-testid="candidate-settings">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
            <Settings className="w-6 h-6 text-cyan-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Configurações</h1>
            <p className="text-slate-600">Gerencie sua conta e preferências</p>
          </div>
        </div>

        {/* Privacy Section */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-600" />
              Privacidade
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">Visível na Busca</p>
                  <p className="text-sm text-slate-500">Seu perfil aparece para as empresas enquanto você tiver cadastro</p>
                </div>
              </div>
              <span className="text-xs font-medium text-green-700" data-testid="settings-always-visible">Sempre ativo</span>
            </div>
          </CardContent>
        </Card>

        {/* Account Section */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-cyan-600" />
              Conta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => navigate('/candidate/profile')}
            >
              <Eye className="w-5 h-5 mr-3" />
              Editar Dados Pessoais
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => setShowPasswordModal(true)}
            >
              <KeyRound className="w-5 h-5 mr-3" />
              Alterar Senha
            </Button>
          </CardContent>
        </Card>

        {/* LGPD Section */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-600" />
              LGPD - Seus Direitos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">Marketing</p>
                  <p className="text-sm text-slate-500">Receber comunicações promocionais</p>
                </div>
              </div>
              <Switch
                checked={consents.marketing || false}
                onCheckedChange={(v) => handleLGPDConsentChange('marketing', v)}
              />
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">Compartilhamento</p>
                  <p className="text-sm text-slate-500">Compartilhar dados com parceiros</p>
                </div>
              </div>
              <Switch
                checked={consents.third_party_sharing || false}
                onCheckedChange={(v) => handleLGPDConsentChange('third_party_sharing', v)}
              />
            </div>
            
            <Separator />
            
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={handleExportData}
              disabled={exporting}
            >
              {exporting ? (
                <Loader2 className="w-5 h-5 mr-3 animate-spin" />
              ) : (
                <Download className="w-5 h-5 mr-3" />
              )}
              Exportar Meus Dados
            </Button>
            
            <a 
              href="/privacy-policy"
              target="_blank"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 text-slate-700"
            >
              <Shield className="w-5 h-5 text-slate-400" />
              <span>Política de Privacidade</span>
              <ExternalLink className="w-4 h-4 ml-auto text-slate-400" />
            </a>
          </CardContent>
        </Card>

        {/* Logout & Delete */}
        <div className="space-y-3">
          <Button 
            variant="outline"
            className="w-full justify-start text-slate-700"
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sair da Conta
          </Button>
          
          {!deletionConfirm ? (
            <Button 
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => setDeletionConfirm(true)}
            >
              <Trash2 className="w-5 h-5 mr-3" />
              Excluir Conta e Dados
            </Button>
          ) : (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-medium text-red-900">Tem certeza?</p>
                    <p className="text-sm text-red-700">Esta ação não pode ser desfeita.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setDeletionConfirm(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={handleDeletionRequest}
                    className="flex-1"
                  >
                    Confirmar Exclusão
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-900">Alterar Senha</h3>
                <button 
                  onClick={() => setShowPasswordModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>Senha Atual</Label>
                  <Input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({
                      ...passwordData, 
                      currentPassword: e.target.value
                    })}
                    placeholder="Digite sua senha atual"
                  />
                </div>
                
                <div>
                  <Label>Nova Senha</Label>
                  <Input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({
                      ...passwordData, 
                      newPassword: e.target.value
                    })}
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>
                
                <div>
                  <Label>Confirmar Nova Senha</Label>
                  <Input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({
                      ...passwordData, 
                      confirmPassword: e.target.value
                    })}
                    placeholder="Digite novamente"
                  />
                </div>
                
                <Button
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                >
                  {changingPassword ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Salvar Nova Senha'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </CandidateLayout>
  );
}
